import { useContext, useState } from 'react';

import { Button } from '@selina-finance/ui';
import mixpanel from 'mixpanel-browser';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { OfferContext } from '@App/contexts/Offer';
import { HttpError } from '@Components/HttpError';
import RateCard from '@Components/RateCard/RateCard';
import { getOffersByProductCodes } from '@Shared/helpers/offer';
import { getFormattedProduct } from '@Shared/helpers/string';
import { InfoNote } from '@Views/eligibility/loan-preferences/components/InfoNote';

export const RateComponent = () => {
    const { t } = useTranslation('loanPreferences');
    const [error, setError] = useState<string | null>(null);
    const [selectedProductCode, setSelectedProductCode] = useState<
        string | null
    >(null);

    const {
        applicationId,
        preferableOffers,
        offers,
        setNeedsAndPreferencesCompleted,
    } = useContext(ApplicationContext);

    const { selectPreferableOffer } = useContext(OfferContext);

    const navigate = useNavigate();

    const filteredOffers = getOffersByProductCodes(offers, preferableOffers);

    const onSubmit = async () => {
        const response = await selectPreferableOffer(
            applicationId,
            selectedProductCode,
        );

        if (response.success) {
            setNeedsAndPreferencesCompleted(true);

            navigate('/eligibility/offer/explore', {
                replace: true,
            });
        }

        if (!response.success) {
            setError(response.error);
        }

        mixpanel.track('View my updated quote');
    };

    return (
        <>
            <div className='mt-12'>
                <div className='text-lg font-sora-bold leading-none mb-2'>
                    {t('rate.label')}
                </div>

                <InfoNote
                    note={t('rate.note')}
                    expandedNote={t('rate.expandedNote')}
                />
                <div className='mt-6'>
                    {filteredOffers.map((offer) => (
                        <RateCard
                            selectable
                            key={offer.productCode}
                            selected={offer.productCode === selectedProductCode}
                            offer={offer}
                            onSelect={() => {
                                setSelectedProductCode(offer.productCode);
                                mixpanel.track('Rate type', {
                                    'Selected option': getFormattedProduct(
                                        offer.product,
                                    ),
                                });
                            }}
                        />
                    ))}
                </div>

                {error && <HttpError error={error} />}

                <div className='flex gap-5 justify-end mt-12'>
                    <Button
                        color='secondary'
                        size='large'
                        className='w-full'
                        disabled={!selectedProductCode}
                        onClick={onSubmit}
                    >
                        View my personalised quote
                    </Button>
                </div>
            </div>
        </>
    );
};
